import type { RawVersionMetadata } from "../../types";
import type { SpecsAutoFormFieldMetadataMap } from "../autoFormMetadata";
import { ObservableModelRoot } from "../observable";
import {
  cloneValue,
  expectObject,
  readBoolean,
  readObjectArray,
  readString,
  splitKnown,
} from "../utils";
import { ProductVersionDecorationTypeModel } from "./ProductVersionDecorationTypeModel";
import { ProductVersionFactoryPricingModel } from "./ProductVersionFactoryPricingModel";
import { ProductVersionGeneratorMetadataModel } from "./ProductVersionGeneratorMetadataModel";
import { ProductVersionImageSideModel } from "./ProductVersionImageSideModel";
import { ProductVersionLabelModel } from "./ProductVersionLabelModel";
import { ProductVersionPackagingModel } from "./ProductVersionPackagingModel";
import { ProductVersionShippingOptionModel } from "./ProductVersionShippingOptionModel";
import { ProductVersionSizeModel } from "./ProductVersionSizeModel";
import { ProductVersionSizeRecommendationsModel } from "./ProductVersionSizeRecommendationsModel";
import { ProductVersionStudioLabelKeysModel } from "./ProductVersionStudioLabelKeysModel";
import { ProductVersionTechpackAnnexeModel } from "./ProductVersionTechpackAnnexeModel";
import {
  PRODUCT_VERSION_FACTORY_OPTIONS,
  PRODUCT_VERSION_PRODUCT_LINE_OPTIONS,
  PRODUCT_VERSION_PRODUCT_SEX_OPTIONS,
  PRODUCT_VERSION_STATUS_OPTIONS,
} from "./productMetadataEnums";

type ProductVersionMetadataModelOptions = {
  debugPathRoot?: string | null;
};

export class ProductVersionMetadataModel extends ObservableModelRoot {
  static autoFormFields: SpecsAutoFormFieldMetadataMap = {
    skuJameo: { label: "SKU Jameo", fieldType: "text" },
    skuPrextra: { label: "SKU Prextra", fieldType: "text" },
    productSex: {
      label: "Product Sex",
      fieldType: "select",
      options: PRODUCT_VERSION_PRODUCT_SEX_OPTIONS,
    },
    productLine: {
      label: "Product Line",
      fieldType: "select",
      options: PRODUCT_VERSION_PRODUCT_LINE_OPTIONS,
    },
    status: {
      label: "Status",
      fieldType: "select",
      options: PRODUCT_VERSION_STATUS_OPTIONS,
    },
    factory: {
      label: "Factory",
      fieldType: "select",
      options: PRODUCT_VERSION_FACTORY_OPTIONS,
    },
    saveLogoPreview: { label: "Save Logo Preview", fieldType: "boolean" },
    deliveryTime: { label: "Delivery time", fieldType: "deliveryTime" },
    pricing: { label: "Pricing", fieldType: "pricing" },
    extra: { label: "Extra JSON", fieldType: "json", minRows: 8 },
  };

  skuJameo!: string;
  skuPrextra!: string;
  productSex!: string;
  productLine!: string;
  status!: string;
  factory!: string;
  saveLogoPreview!: boolean;
  deliveryTime!: Record<string, unknown>;
  pricing!: Record<string, unknown>;
  labels!: ProductVersionLabelModel[];
  packaging!: ProductVersionPackagingModel | null;
  techpackAnnexe!: ProductVersionTechpackAnnexeModel[];
  shipping!: ProductVersionShippingOptionModel[];
  sizeRecommendations!: ProductVersionSizeRecommendationsModel | null;
  factoryPricing!: ProductVersionFactoryPricingModel | null;
  sizes!: ProductVersionSizeModel[];
  availableImageSides!: ProductVersionImageSideModel[];
  availableDecorationTypes!: ProductVersionDecorationTypeModel[];
  studioLabelKeys!: ProductVersionStudioLabelKeysModel | null;
  generator!: ProductVersionGeneratorMetadataModel | null;
  extra!: Record<string, unknown>;

  constructor({
    debugPathRoot = null,
  }: ProductVersionMetadataModelOptions = {}) {
    super(debugPathRoot);
    this.defineObservableValue("skuJameo", "");
    this.defineObservableValue("skuPrextra", "");
    this.defineObservableValue("productSex", "");
    this.defineObservableValue("productLine", "");
    this.defineObservableValue("status", "");
    this.defineObservableValue("factory", "");
    this.defineObservableValue("saveLogoPreview", false);
    this.defineObservableValue("deliveryTime", {});
    this.defineObservableValue("pricing", {});
    this.defineObservableValue("labels", []);
    this.defineObservableValue("packaging", null);
    this.defineObservableValue("techpackAnnexe", []);
    this.defineObservableValue("shipping", []);
    this.defineObservableValue("sizeRecommendations", null);
    this.defineObservableValue("factoryPricing", null);
    this.defineObservableValue("sizes", []);
    this.defineObservableValue("availableImageSides", []);
    this.defineObservableValue("availableDecorationTypes", []);
    this.defineObservableValue("studioLabelKeys", null);
    this.defineObservableValue("generator", null);
    this.defineObservableValue("extra", {});
  }

  restore(snapshot: RawVersionMetadata | null | undefined) {
    const source = snapshot
      ? expectObject(snapshot, "product version metadata")
      : {};
    this.skuJameo = readString(source, "skuJameo");
    this.skuPrextra = readString(source, "skuPrextra");
    this.productSex = readString(source, "productSex");
    this.productLine = readString(source, "productLine");
    this.status = readString(source, "status").toLowerCase();
    this.factory = readString(source, "factory");
    this.saveLogoPreview = readBoolean(source, "saveLogoPreview", false);
    this.deliveryTime =
      source.deliveryTime &&
      typeof source.deliveryTime === "object" &&
      !Array.isArray(source.deliveryTime)
        ? cloneValue(source.deliveryTime as Record<string, unknown>)
        : {};
    this.pricing =
      source.pricing &&
      typeof source.pricing === "object" &&
      !Array.isArray(source.pricing)
        ? cloneValue(source.pricing as Record<string, unknown>)
        : {};
    this.labels = Array.isArray(source.labels)
      ? source.labels.map((entry) =>
          new ProductVersionLabelModel().restore(entry)
        )
      : [];
    this.packaging =
      source.packaging &&
      typeof source.packaging === "object" &&
      !Array.isArray(source.packaging)
        ? new ProductVersionPackagingModel().restore(source.packaging)
        : new ProductVersionPackagingModel();
    this.techpackAnnexe = readObjectArray(source, "techpackAnnexe").map(
      (entry) => new ProductVersionTechpackAnnexeModel().restore(entry)
    );
    this.shipping = Array.isArray(source.shipping)
      ? source.shipping.map((entry) =>
          new ProductVersionShippingOptionModel().restore(entry)
        )
      : [];
    this.sizeRecommendations =
      source.sizeRecommendations &&
      typeof source.sizeRecommendations === "object" &&
      !Array.isArray(source.sizeRecommendations)
        ? new ProductVersionSizeRecommendationsModel().restore(
            source.sizeRecommendations
          )
        : new ProductVersionSizeRecommendationsModel();
    this.factoryPricing =
      source.factoryPricing &&
      typeof source.factoryPricing === "object" &&
      !Array.isArray(source.factoryPricing)
        ? new ProductVersionFactoryPricingModel().restore(source.factoryPricing)
        : new ProductVersionFactoryPricingModel();
    this.sizes = Array.isArray(source.sizes)
      ? source.sizes.map((entry) =>
          new ProductVersionSizeModel().restore(entry)
        )
      : [];
    this.availableImageSides = Array.isArray(source.availableImageSides)
      ? source.availableImageSides.map((entry) =>
          new ProductVersionImageSideModel().restore(entry)
        )
      : [];
    this.availableDecorationTypes = Array.isArray(
      source.availableDecorationTypes
    )
      ? source.availableDecorationTypes.map((entry) =>
          new ProductVersionDecorationTypeModel().restore(entry)
        )
      : [];
    this.studioLabelKeys =
      source.studioLabelKeys &&
      typeof source.studioLabelKeys === "object" &&
      !Array.isArray(source.studioLabelKeys)
        ? new ProductVersionStudioLabelKeysModel().restore(
            source.studioLabelKeys
          )
        : new ProductVersionStudioLabelKeysModel();
    this.generator =
      source.generator &&
      typeof source.generator === "object" &&
      !Array.isArray(source.generator)
        ? new ProductVersionGeneratorMetadataModel().restore(source.generator)
        : new ProductVersionGeneratorMetadataModel();
    this.extra = splitKnown(source, [
      "skuJameo",
      "skuPrextra",
      "productSex",
      "productLine",
      "status",
      "factory",
      "saveLogoPreview",
      "deliveryTime",
      "pricing",
      "labels",
      "packaging",
      "techpackAnnexe",
      "shipping",
      "sizeRecommendations",
      "factoryPricing",
      "sizes",
      "availableImageSides",
      "availableDecorationTypes",
      "studioLabelKeys",
      "generator",
    ]);
    return this;
  }

  save(): RawVersionMetadata {
    return {
      ...cloneValue(this.extra),
      ...(this.skuJameo ? { skuJameo: this.skuJameo } : {}),
      ...(this.skuPrextra ? { skuPrextra: this.skuPrextra } : {}),
      ...(this.productSex ? { productSex: this.productSex } : {}),
      ...(this.productLine ? { productLine: this.productLine } : {}),
      ...(this.status ? { status: this.status } : {}),
      ...(this.factory ? { factory: this.factory } : {}),
      ...(this.saveLogoPreview ? { saveLogoPreview: true } : {}),
      ...(Object.keys(this.deliveryTime).length > 0
        ? { deliveryTime: cloneValue(this.deliveryTime) }
        : {}),
      ...(Object.keys(this.pricing).length > 0
        ? { pricing: cloneValue(this.pricing) }
        : {}),
      labels: this.labels.map((entry) => entry.save()),
      packaging: this.packaging ? this.packaging.save() : {},
      ...(this.techpackAnnexe.length > 0
        ? { techpackAnnexe: this.techpackAnnexe.map((entry) => entry.save()) }
        : {}),
      shipping: this.shipping.map((entry) => entry.save()),
      ...(this.sizeRecommendations &&
      Object.keys(this.sizeRecommendations.save()).length > 0
        ? { sizeRecommendations: this.sizeRecommendations.save() }
        : {}),
      ...(this.factoryPricing &&
      Object.keys(this.factoryPricing.save()).length > 0
        ? { factoryPricing: this.factoryPricing.save() }
        : {}),
      sizes: this.sizes.map((entry) => entry.save()),
      availableImageSides: this.availableImageSides.map((entry) =>
        entry.save()
      ),
      availableDecorationTypes: this.availableDecorationTypes.map((entry) =>
        entry.save()
      ),
      ...(this.studioLabelKeys &&
      Object.keys(this.studioLabelKeys.save()).length > 0
        ? { studioLabelKeys: this.studioLabelKeys.save() }
        : {}),
      ...(this.generator && Object.keys(this.generator.save()).length > 0
        ? { generator: this.generator.save() }
        : {}),
    };
  }
}
